import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import Lenis from "lenis";

type ScrollTarget = string | HTMLElement | number;
type ScrollTo = (target: ScrollTarget, opts?: { offset?: number }) => void;

const ScrollCtx = createContext<ScrollTo>(() => {});
export const useSmoothScroll = () => useContext(ScrollCtx);

// Défilement « premium » façon Apple/Airbnb (Lenis), désactivé si l'utilisateur
// préfère réduire les animations.
export default function SmoothScroll({ children }: { children: ReactNode }) {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;

    let rafId = 0;
    const loop = (time: number) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollTo: ScrollTo = (target, opts) => {
    const offset = opts?.offset ?? -72; // compense la navbar fixe
    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, { offset });
      return;
    }
    // Repli (reduced-motion / Lenis off)
    if (typeof target === "number") {
      window.scrollTo({ top: target + offset, behavior: "smooth" });
    } else if (typeof target === "string") {
      const el = document.querySelector(target);
      if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth" });
    } else {
      target.scrollIntoView({ behavior: "smooth" });
    }
  };

  return <ScrollCtx.Provider value={scrollTo}>{children}</ScrollCtx.Provider>;
}
